import React, { useCallback, useMemo, useState } from "react"
import { useInView } from "react-intersection-observer"

import { Skeleton } from "@coval/ui"
import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  type SortDescriptor,
} from "@heroui/react"

import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

interface RunDetailsTableRow {
  simulationOutputId: string
  runId: string
  status: string
  createdAt: Date
  createdAtString: string
  customerMetadata: Record<string, unknown>
  rawMetricValue: number
  metricId: string
  [metricDisplayName: string]: Date | Record<string, unknown> | React.ReactNode
}

interface RunDetailsTableProps {
  onSimulationIdClick?: (simulationOutputId: string, runId: string) => void
  isLoading?: boolean
  // Pre-processed data from the custom hook
  rows: RunDetailsTableRow[]
  columns: Array<{
    key: string
    label: string
    sortable: boolean
  }>
}

const RunDetailsTable: React.FC<RunDetailsTableProps> = ({
  onSimulationIdClick,
  isLoading,
  rows,
  columns,
}) => {
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "createdAt",
    direction: "descending",
  })

  const [displayedRows, setDisplayedRows] = useState<RunDetailsTableRow[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const itemsPerPage = 15

  // Sort the processed rows
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      let aValue, bValue

      if (sortDescriptor.column === "createdAt") {
        aValue = a.createdAt.getTime()
        bValue = b.createdAt.getTime()
      } else if (sortDescriptor.column === "status") {
        aValue = a.status
        bValue = b.status
      } else {
        // For any other column (including dynamic metric columns)
        aValue = a[sortDescriptor.column] ?? ""
        bValue = b[sortDescriptor.column] ?? ""
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (sortDescriptor.direction === "descending") {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
      }
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
    })
  }, [rows, sortDescriptor])

  // Load more rows when scrolling
  const loadMoreRows = useCallback(() => {
    const startIndex = currentPage * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const newRows = sortedRows.slice(startIndex, endIndex)

    setDisplayedRows((prev) => [...prev, ...newRows])
    setCurrentPage((prev) => prev + 1)
  }, [currentPage, sortedRows])

  // Initialize displayed rows
  React.useEffect(() => {
    setDisplayedRows(sortedRows.slice(0, itemsPerPage))
    setCurrentPage(1)
  }, [sortedRows])

  // Infinite scroll setup
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.1,
  })

  // Load more when the load more element comes into view
  React.useEffect(() => {
    if (inView && displayedRows.length < sortedRows.length) {
      loadMoreRows()
    }
  }, [inView, displayedRows.length, sortedRows.length, loadMoreRows])

  const handleSortChange = (descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor)
    // Reset displayed rows when sorting changes
    setDisplayedRows(sortedRows.slice(0, itemsPerPage))
    setCurrentPage(1)
  }

  const renderSimulationIdCell = (
    simulationOutputId: string,
    runId: string,
  ) => (
    <button
      onClick={() => onSimulationIdClick?.(simulationOutputId, runId)}
      className="text-foreground hover:text-coval-text hover:underline cursor-pointer font-mono text-sm"
      title={simulationOutputId}
      aria-label={`View details for simulation ${simulationOutputId}`}
    >
      {simulationOutputId}
    </button>
  )

  const renderMetadataCell = (metadata: Record<string, unknown>) => (
    <div
      className={`w-full flex flex-wrap gap-1 overflow-hidden ${
        Object.keys(metadata).length > 0 ? "justify-start" : "justify-center"
      }`}
    >
      {Object.entries(metadata).map(([key, value]) => (
        <Chip key={key} size="sm" variant="flat">
          <span className="font-semibold">{key}:</span>{" "}
          {typeof value === "object"
            ? JSON.stringify(value)
            : stringifyUnknownValue(value)}
        </Chip>
      ))}
      {Object.keys(metadata).length === 0 && (
        <span className="text-muted-foreground text-sm">No metadata</span>
      )}
    </div>
  )

  const renderCell = (column: { key: string }, row: RunDetailsTableRow) => {
    switch (column.key) {
      case "simulationOutputId":
        return renderSimulationIdCell(row.simulationOutputId, row.runId)
      case "createdAt":
        return row.createdAtString
      case "customerMetadata":
        return renderMetadataCell(row.customerMetadata)
      default:
        return row[column.key] as React.ReactNode
    }
  }

  const getCellClassName = (columnKey: string) => {
    if (columnKey === "simulationOutputId") {
      return "text-left"
    }
    return "text-center"
  }

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-center text-coval-text-muted">
          <div className="text-sm">
            No runs found for the selected data point
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col w-full gap-4 overflow-auto">
      <Table
        aria-label="Run details table"
        sortDescriptor={sortDescriptor}
        onSortChange={handleSortChange}
        classNames={{
          base: "max-h-[520px] overflow-scroll",
          th: "py-2 whitespace-normal text-center wrap-break-word sticky top-0 bg-background z-10",
          td: "py-4 whitespace-normal wrap-break-word",
          table: "w-full min-h-[400px]",
        }}
      >
        <TableHeader>
          {columns.map((column) => (
            <TableColumn key={column.key} allowsSorting={column.sortable}>
              {column.label}
            </TableColumn>
          ))}
        </TableHeader>
        <TableBody
          items={displayedRows}
          emptyContent="No runs found for the selected data point"
        >
          {(row: RunDetailsTableRow) => (
            <TableRow key={`${row.simulationOutputId}-${row.metricId}`}>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  className={getCellClassName(column.key)}
                >
                  {renderCell(column, row)}
                </TableCell>
              ))}
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Load more trigger */}
      {displayedRows.length < sortedRows.length && (
        <div ref={loadMoreRef} className="flex justify-center py-4">
          <Skeleton className="h-4 w-32 rounded-md" />
        </div>
      )}
    </div>
  )
}

export { RunDetailsTable }
